// Main-menu structure (SEO audit batch 2, Part 3/4). One source for the desktop
// dropdowns, the phone menu groups and the footer's city ordering, built from the
// light data indexes so the shared shell bundle stays small.
//
// Every entry is a real route. Labels are the menu's short forms; the pages keep
// their own full names.
import { SERVICE_INDEX } from "@/data/services-index";
import { CITY_INDEX, COUNTIES } from "@/data/cities-index";
import { devWarn } from "@/lib/logger";

const service = (slug, label) => {
  if (!SERVICE_INDEX.some((s) => s.slug === slug)) devWarn(`[nav] unknown service slug "${slug}"`);
  return { to: `/services/${slug}`, label };
};

export const NAV_SERVICES = {
  homeowners: {
    heading: "Homeowners",
    links: [
      service("emergency-plumbing", "Emergency Plumbing (24/7)"),
      service("drain-cleaning-sewer", "Drain Cleaning & Sewer"),
      service("water-heaters", "Water Heaters"),
      service("repiping-pipe-repair", "Repiping & Pipe Repair"),
      service("fixture-installation", "Fixture Installation"),
      service("water-sewer-line-replacement", "Water & Sewer Lines"),
      service("gas-lines", "Gas Lines"),
    ],
  },
  builders: {
    heading: "Builders & property managers",
    links: [
      service("commercial-new-construction", "New Construction & Commercial"),
      { to: "/estimate/bid-request", label: "Request a Bid" },
    ],
  },
  all: { to: "/services", label: "All services" },
};

export const NAV_PRICING = [
  { to: "/estimate", label: "60-Second Estimator" },
  { to: "/pricing", label: "Pricing Guide" },
  { to: "/financing", label: "Financing" },
];

export const NAV_ABOUT = [
  { to: "/about", label: "About Us" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "Blog & Resources" },
  { to: "/contact", label: "Contact" },
];

// Editorial city order per county (largest / best-known first); unknown slugs
// keep data order. Shared by the Locations menu and the footer Service Areas.
export const CITY_ORDER = {
  king: ["seattle", "bellevue", "kent", "auburn", "renton", "burien", "des-moines", "seatac", "tukwila", "covington", "maple-valley", "issaquah", "sammamish", "redmond", "kirkland", "bothell", "kenmore", "shoreline", "mercer-island"],
  pierce: ["tacoma", "puyallup", "lakewood", "university-place", "gig-harbor", "fife", "sumner", "bonney-lake", "spanaway", "parkland", "graham", "frederickson"],
  snohomish: ["everett", "lynnwood", "edmonds", "mukilteo", "mill-creek", "mountlake-terrace", "marysville", "lake-stevens", "arlington", "monroe"],
};

const FEDERAL_WAY = { key: "federal-way", to: "/locations/federal-way" };

/**
 * City page links of a county in editorial order. Federal Way (the HQ, a
 * /locations page rather than a /plumber page) belongs to the King County list:
 * `federalWay: "first"` puts it first labelled "Federal Way (HQ)" (menu);
 * `"after-auburn"` keeps the footer's historical position and plain label.
 */
export const cityLinksByCounty = (countyId, { federalWay = "first", testIdPrefix = "nav-area" } = {}) => {
  const order = CITY_ORDER[countyId] || [];
  const items = CITY_INDEX.filter((c) => c.countyId === countyId)
    .map((c) => ({ key: c.slug, to: `/plumber/${c.slug}`, label: c.name, testId: `${testIdPrefix}-${c.slug}` }))
    .sort((a, b) => {
      const ia = order.indexOf(a.key), ib = order.indexOf(b.key);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  if (countyId === "king") {
    if (federalWay === "first") {
      items.unshift({ ...FEDERAL_WAY, label: "Federal Way (HQ)", testId: `${testIdPrefix}-federal-way` });
    } else {
      const at = items.findIndex((i) => i.key === "auburn") + 1;
      items.splice(at, 0, { ...FEDERAL_WAY, label: "Federal Way", testId: `${testIdPrefix}-federal-way` });
    }
  }
  return items;
};

export const NAV_LOCATIONS = {
  counties: COUNTIES.map((c) => ({ id: c.id, name: c.name, to: `/locations#${c.id}` })),
  alsoServing: [
    { key: "vancouver", to: "/locations/vancouver", label: "Vancouver & Clark County" },
    { key: "spokane", to: "/locations/spokane", label: "Spokane" },
  ],
  all: { to: "/locations", label: "All service areas" },
};

// The five top-level items. `match` decides which item is highlighted for the
// current URL (prefix match on the pathname; the most specific wins).
export const NAV_ITEMS = [
  { id: "services", label: "Services", to: "/services", match: ["/services", "/estimate/bid-request"] },
  { id: "locations", label: "Locations", to: "/locations", match: ["/locations", "/plumber"] },
  { id: "pricing", label: "Pricing", to: "/pricing", match: ["/pricing", "/estimate", "/financing"] },
  { id: "work", label: "Our Work", to: "/projects", match: ["/projects"] },
  { id: "about", label: "About", to: "/about", match: ["/about", "/reviews", "/faq", "/blog", "/contact"] },
];

const matches = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Id of the nav item that owns `pathname`, or null (home, admin, 404 ...). */
export const activeNavItem = (pathname) => {
  let best = null;
  let bestLen = -1;
  for (const item of NAV_ITEMS) {
    for (const prefix of item.match) {
      if (matches(pathname, prefix) && prefix.length > bestLen) {
        best = item.id;
        bestLen = prefix.length;
      }
    }
  }
  return best;
};
